// HolyWheelDisc.tsx

import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { formatCompactCurrencyMinorUnits } from "../../../../../logic/helpers/currencyAmount";
import DiscArtwork from "../../../../../assets/static/images/holy-wheel/wheel-disc.svg?react";
import {
    type HolyWheelPrize,
    PRIZE_THUMBNAILS,
    BUNDLE_SIZE_INDICATORS,
    getHolyWheelPrizeAmount,
    type HolyWheelMultiplier,
} from "../../../../../logic/helpers/holyWheelPrizes";
import "./HolyWheelDisc.scss";

// All dimensions are percentages of the colored slice radius, from tip to rim.
// The text slot's inner edge is the anchor; its outer edge supports the PNG.
const PRIZE_LAYOUT = {
    textOffsetPercent: 52,
    textHeightPercent: 9,
    thumbnailWidthPercent: 34,
};

/** Segment 0 is just clockwise of 12 o'clock; remaining segments run clockwise. */
export type HolyWheelSliceColors = readonly [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
];

export const DEFAULT_HOLY_WHEEL_SLICE_COLORS: HolyWheelSliceColors = [
    "#6ABBFC",
    "#FED55A",
    "#FE8D88",
    "#9E99FC",
    "#68BBFD",
    "#63ECDF",
    "#FDD358",
    "#68BBFD",
    "#A19CFC",
    "#FE8B86",
    "#FED65C",
    "#63ECDF",
];

interface SliceColorStyle extends CSSProperties {
    [property: `--holy-wheel-slice-${number}`]: string;
}

interface PrizeLayoutStyle extends CSSProperties {
    "--prize-text-offset": string;
    "--prize-text-height": string;
    "--prize-thumbnail-width": string;
}

const prizeLayoutStyle: PrizeLayoutStyle = {
    "--prize-text-offset": `${PRIZE_LAYOUT.textOffsetPercent}%`,
    "--prize-text-height": `${PRIZE_LAYOUT.textHeightPercent}%`,
    "--prize-thumbnail-width": `${PRIZE_LAYOUT.thumbnailWidthPercent}%`,
};

interface HolyWheelDiscProps {
    prizes: readonly HolyWheelPrize[];
    sliceColors?: HolyWheelSliceColors;
    multiplier?: HolyWheelMultiplier;
}

export function HolyWheelDisc({
    prizes,
    sliceColors = DEFAULT_HOLY_WHEEL_SLICE_COLORS,
    multiplier = 1,
}: HolyWheelDiscProps) {
    const { t, i18n } = useTranslation();
    const style: SliceColorStyle = {};
    sliceColors.forEach((color, index) => {
        style[`--holy-wheel-slice-${index}`] = color;
    });

    return (
        <div className="holy-wheel-disc" style={style}>
            <DiscArtwork className="holy-wheel-disc__artwork" />
            <div
                className="holy-wheel-disc__prizes"
                style={prizeLayoutStyle}
                aria-hidden="true"
            >
                {prizes.map((prize, index) => (
                    <div
                        key={index}
                        className="holy-wheel-disc__prize"
                        data-prize-index={index}
                        data-prize-kind={prize.kind}
                        style={{
                            transform: `rotate(${(index + 0.5) * (360 / prizes.length)}deg)`,
                        }}
                    >
                        <div className="holy-wheel-disc__prize-content">
                            <img
                                className="holy-wheel-disc__prize-thumbnail"
                                src={
                                    prize.wheelImageUrl ??
                                    prize.imageUrl ??
                                    PRIZE_THUMBNAILS[prize.kind]
                                }
                                width={840}
                                height={780}
                                alt=""
                                draggable={false}
                            />
                            <div
                                className="holy-wheel-disc__prize-text"
                                dir="ltr"
                            >
                                {"amount" in prize &&
                                    t("holyWheel.prizeAmount", {
                                        amount: formatCompactCurrencyMinorUnits(
                                            getHolyWheelPrizeAmount(
                                                prize,
                                                multiplier,
                                            ) ?? "0",
                                            "BEACH_COINS",
                                            i18n.resolvedLanguage ?? "en",
                                        ).replace(/K$/, "k"),
                                    })}
                                {"bundleSize" in prize && (
                                    <img
                                        className="holy-wheel-disc__prize-indicator"
                                        src={
                                            BUNDLE_SIZE_INDICATORS[
                                                prize.bundleSize
                                            ]
                                        }
                                        width={650}
                                        height={252}
                                        alt=""
                                        draggable={false}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
